import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Box, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { AlignHorizontalLeft, BarChart as ColumnsIcon, EditNote, EventNote } from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import EmptyState from '../common/EmptyState';
import { SectionCard } from '../common/SectionCard';
import useAutoFetch from '../../hooks/useAutoFetch';
import { generateChartColors, getExamClassPerformance } from '../../services/analysisService';
import { AnalysisScope, pickId } from './useAnalysisScope';
import { GradeScale, useGradeScale } from './useGradeScale';
import { Choice, ChoiceToggle, ScoreBars, ScoreItem } from './chartKit';
import { PickChips, ResultBody, ResultHeading, ScopeFilters, StatStrip } from './AnalysisParts';
import FilterBar from '../common/FilterBar';
import { pct } from './trend';

const ALL_SUBJECTS = 'all';

type Order = 'rank' | 'roll';
type Layout = 'rows' | 'columns';

const ORDERS: Array<Choice<Order>> = [
  { value: 'rank', label: 'Rank' },
  { value: 'roll', label: 'Roll no.' },
];
const LAYOUTS: Array<Choice<Layout>> = [
  { value: 'rows', label: 'Horizontal bars', icon: <AlignHorizontalLeft sx={{ fontSize: 18 }} /> },
  { value: 'columns', label: 'Columns', icon: <ColumnsIcon sx={{ fontSize: 18 }} /> },
];

/** The last order and layout chosen, kept while moving around the app. */
const chartPrefs: { order: Order; layout: Layout } = { order: 'rank', layout: 'rows' };

/** Roll numbers are strings; compare them as numbers where they are numbers. */
const byRoll = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/** How the class spread across grade bands (the grades the server gave), as one segmented bar with counts. */
const GradeSpread: React.FC<{ letters: string[]; grades: GradeScale }> = ({ letters, grades }) => {
  const theme = useTheme();
  const bands = grades.bands
    .map((g) => ({ grade: g, count: letters.filter((l) => l === g.name).length }))
    .filter((b) => b.count > 0);
  return (
    <>
      <Box sx={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', gap: '2px', bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
        {bands.map((b) => (
          <Tooltip key={b.grade._id} title={`${b.grade.name}: ${b.count} student${b.count === 1 ? '' : 's'}`}>
            <Box sx={{ flex: b.count, bgcolor: b.grade.color }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
        {bands.length === 0 && <Typography variant="body2" color="text.secondary">Grades appear once marks are entered.</Typography>}
        {bands.map((b) => (
          <Box key={b.grade._id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.grade.color }} />
            <Typography variant="body2" fontWeight={600}>{b.grade.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {b.count} · {Math.round((b.count / letters.length) * 100)}%
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};

/** One exam's results for a class, students ranked. Opens on the class's latest exam. */
const ClassPerformanceView: React.FC<{ scope: AnalysisScope }> = ({ scope }) => {
  const navigate = useNavigate();
  const grades = useGradeScale();
  const { sessionId, classId, centerId, exams } = scope;
  const examId = pickId(scope.raw('exam'), exams);
  const exam = exams.find((e) => e._id === examId);
  // Only the subjects this exam sets for the class, in the exam's order.
  const subjects = useMemo(() => {
    const byId = new Map(scope.options.subjects.map((s) => [s._id, s]));
    return (exam?.subjectIds ?? []).flatMap((id) => byId.get(id) ?? []);
  }, [exam, scope.options.subjects]);
  const wantedSubject = scope.raw('subject');
  const subjectId = subjects.some((s) => s._id === wantedSubject) ? wantedSubject : ALL_SUBJECTS;

  const result = useAutoFetch(
    examId ? `${sessionId}|${classId}|${examId}|${subjectId}` : null,
    () => getExamClassPerformance({ sessionId, classId, examId, subjectId, centerId: centerId || undefined }),
  );

  const [order, setOrder] = useState<Order>(chartPrefs.order);
  const [layout, setLayout] = useState<Layout>(chartPrefs.layout);
  const changeOrder = (v: Order) => { chartPrefs.order = v; setOrder(v); };
  const changeLayout = (v: Layout) => { chartPrefs.layout = v; setLayout(v); };

  // Rank is position by score (ties share a rank), shown whatever the order.
  // Pass/fail comes from the server, under the school's result rules.
  const theme = useTheme();
  const items = useMemo<ScoreItem[]>(() => {
    const students = result.data?.students ?? [];
    const subjectName = new Map(scope.options.subjects.map((sub) => [sub._id, sub.name]));
    const note = (s: typeof students[number]) => {
      if (s.outcome === 'PASS') return { text: 'Result: Pass', color: theme.palette.success.main };
      if (s.outcome === 'PENDING') return { text: 'Result: In progress', color: theme.palette.text.secondary };
      if (s.outcome !== 'FAIL') return undefined;
      const failed = s.failedSubjectIds.map((id) => subjectName.get(id)).filter(Boolean);
      return { text: `Result: Fail${failed.length ? ` — ${failed.join(', ')}` : ''}`, color: theme.palette.error.main };
    };
    return [...students]
      .sort((a, b) => (order === 'roll' ? byRoll(String(a.rollNumber), String(b.rollNumber)) : b.percentage - a.percentage))
      .map((s) => ({
        id: String(s._id),
        name: s.name,
        value: s.percentage,
        detail: `Rank ${s.rank} of ${students.length} · Roll ${s.rollNumber} · ${s.obtainedMarks} / ${s.totalMarks} marks`,
        note: note(s),
        flagged: s.outcome === 'FAIL',
      }));
  }, [result.data, order, scope.options.subjects, theme]);
  const outcomes = result.data?.students.map((s) => s.outcome) ?? [];
  const passed = outcomes.filter((o) => o === 'PASS').length;
  const failedCount = outcomes.filter((o) => o === 'FAIL').length;
  const inProgress = outcomes.filter((o) => o === 'PENDING').length;

  const subjectChips = useMemo(() => {
    const colors = generateChartColors(subjects.length);
    return subjects.map((s, i) => ({ id: s._id, name: s.name, color: colors[i] }));
  }, [subjects]);
  const pickSubject = (id: string) => scope.set('subject', id === ALL_SUBJECTS ? '' : id);

  const values = useMemo(() => items.map((i) => i.value), [items]);
  const letters = useMemo(() => (result.data?.students ?? []).map((s) => s.grade), [result.data]);
  const average = result.data?.classAverage ?? null;
  const ranked = useMemo(() => [...items].sort((a, b) => b.value - a.value), [items]);
  const top = ranked[0];
  const bottom = ranked[ranked.length - 1];
  const atOrAbove = average == null ? 0 : values.filter((v) => v >= average).length;

  const { className } = scope;
  const subjectName = subjectId === ALL_SUBJECTS ? 'All subjects' : subjects.find((s) => s._id === subjectId)?.name ?? '';

  const openStudent = (item: ScoreItem) => {
    const params = new URLSearchParams({ session: sessionId, class: classId, student: item.id });
    navigate(`/app/analysis/student-progress?${params.toString()}`);
  };

  // An exam (or one subject of it) without marks keeps the page's layout; only the chart says so.
  const noMarks = !!exam && (result.notFound || (!!result.data && items.length === 0));
  const noMarksYet = exam && (
    <EmptyState
      dense
      icon={<EditNote />}
      title={`No ${subjectId !== ALL_SUBJECTS ? `${subjectName} ` : ''}marks for ${exam.name} yet`}
      description={`Scores for ${className} show here once marks are entered.`}
      action={{ label: 'Enter marks', icon: <EditNote />, onClick: () => navigate(`/app/examination/marks?exam=${encodeURIComponent(exam.name)}`) }}
      secondaryAction={subjectId !== ALL_SUBJECTS ? { label: 'Show all subjects', onClick: () => pickSubject(ALL_SUBJECTS) } : undefined}
      sx={{ border: 'none', py: 6 }}
    />
  );

  const empty = !classId ? (
    <EmptyState icon={<EventNote />} title="No classes to analyse" description="Add classes and publish an exam to see class results." />
  ) : !exam ? (
    <EmptyState
      icon={<EventNote />}
      title={`No published exams for ${className}`}
      description="Publish an exam for this class and enter its marks to see how the class did."
      action={{ label: 'Manage exams', icon: <EventNote />, onClick: () => navigate('/app/examination/manage') }}
    />
  ) : null;

  return (
    <>
      <FilterBar loading={result.loading}>
        <ScopeFilters scope={scope} />
        <SearchableSelect
          label="Exam" value={examId} clearable={false} minWidth={220} disabled={!exams.length}
          onChange={(v) => scope.set('exam', v)}
          options={exams.map((e) => ({ value: e._id, label: e.name, secondary: e.startDate ? format(new Date(e.startDate), 'd MMM yyyy') : undefined }))}
        />
      </FilterBar>

      <ResultBody state={result} empty={empty}>
        <ResultHeading
          title={`${exam?.name ?? ''} · ${className}`}
          subtitle={[subjectName, exam?.startDate && format(new Date(exam.startDate), 'd MMM yyyy')].filter(Boolean).join(' · ')}
        />
        <StatStrip stats={[
          {
            label: 'Class average',
            value: pct(average),
            color: average != null ? grades.color(average) : undefined,
            hint: average != null ? `${atOrAbove} of ${values.length} at or above it` : 'No marks yet',
          },
          { label: 'Highest', value: top ? pct(top.value) : '—', color: top ? grades.color(top.value) : undefined, hint: top?.name ?? 'No marks yet' },
          { label: 'Lowest', value: bottom ? pct(bottom.value) : '—', color: bottom ? grades.color(bottom.value) : undefined, hint: bottom?.name ?? 'No marks yet' },
          result.data?.passRules
            ? {
              label: 'Passed',
              value: `${passed} of ${values.length}`,
              color: failedCount ? undefined : theme.palette.success.main,
              hint: [failedCount && `${failedCount} failed`, inProgress && `${inProgress} in progress`].filter(Boolean).join(' · ') || 'Everyone passed',
            }
            : { label: 'Students graded', value: values.length, hint: subjectName },
        ]} />

        <SectionCard title="Grade spread" sx={{ mb: 2.5 }}>
          <GradeSpread letters={letters} grades={grades} />
        </SectionCard>

        <SectionCard
          title={order === 'rank' ? 'Ranking' : 'By roll number'}
          action={
            <Stack direction="row" spacing={1}>
              <ChoiceToggle options={ORDERS} value={order} onChange={changeOrder} />
              <ChoiceToggle options={LAYOUTS} value={layout} onChange={changeLayout} />
            </Stack>
          }
        >
          {subjects.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <PickChips options={subjectChips} value={subjectId} allId={ALL_SUBJECTS} onChange={pickSubject} />
            </Box>
          )}
          {noMarks ? noMarksYet : <ScoreBars
            items={items} average={average} averageLabel="Class average" grades={grades}
            columns={layout === 'columns'} keepOrder rowHeight={32}
            onSelect={openStudent} selectHint="Click to see this student's progress"
          />}
        </SectionCard>
      </ResultBody>
    </>
  );
};

export default ClassPerformanceView;
